import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { AlertController, ModalController } from '@ionic/angular';
import { select, Store } from '@ngrx/store';

import { groupBy, isNil, isUndefined, map as lodashMap } from 'lodash-es';
import { DateTime } from 'luxon';
import { map, Observable, of, tap } from 'rxjs';
import { concatMap, filter } from 'rxjs/operators';

import { AddressEnum } from '../../../../enums/address';
import { MainTabBasePathEnum } from '../../../../enums/main-tabs';
import * as fromPropertyProfileActions from '../../../../store/actions/property-profile.actions';
import { ProfileTransactionHistory, PropertyProfile } from '../../../../store/models/property-profile.model';
import * as fromDataProviderSelectors from '../../../../store/selectors/data-provider.selectors';
import * as fromOfficeSelectors from '../../../../store/selectors/office.selectors';
import * as fromPropertyProfileSelectors from '../../../../store/selectors/property-profile.selectors';
import * as fromUserSelectors from '../../../../store/selectors/user.selectors';
import { AddressPipe } from '../../../pipes/address.pipe';
import { SendDocumentModalComponent } from './send-document-modal/send-document-modal.component';

interface TransactionHistoryExtended extends ProfileTransactionHistory {
  showDocumentNumber?: boolean;
  title?: string;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'red-profile-transaction-history',
  standalone: false,
  templateUrl: './transaction-history.page.html'
})
export class TransactionHistoryPageComponent {
  private store = inject(Store);
  private activatedRoute = inject(ActivatedRoute);
  private alertController = inject(AlertController);
  private modalController = inject(ModalController);
  private addressPipe = inject(AddressPipe);

  isDataTrace = false;
  userEmail: string | null = null;
  profile: PropertyProfile | null = null;

  profileId: string | null;
  transactionHistoryGroups$: Observable<{ data: TransactionHistoryExtended[]; title: string }[]>;
  documentLinksEnabled$: Observable<boolean>;
  defaultBackHref: string | undefined;

  constructor() {
    this.defaultBackHref = this.activatedRoute.snapshot.data['defaultBackHref'] ?? MainTabBasePathEnum.PropertySearch;
    this.profileId = this.activatedRoute.snapshot.paramMap.get('id');

    of(this.profileId)
      .pipe(
        filter((id) => !isNil(id)),
        concatMap((id) => this.store.pipe(select(fromPropertyProfileSelectors.selectPropertyProfile(id)))),
        filter((propertyProfile) => !isUndefined(propertyProfile)),
        tap((propertyProfile) => {
          this.profile = propertyProfile;
        })
      )
      .subscribe();

    this.transactionHistoryGroups$ = of(this.profileId).pipe(
      filter((id) => !isNil(id)),
      concatMap((id) => this.store.pipe(select(fromPropertyProfileSelectors.selectPropertyProfile(id)))),
      filter((propertyProfile) => !isUndefined(propertyProfile)),
      map((propertyProfile) =>
        lodashMap(groupBy(propertyProfile?.TransHistory, 'RecordingDate'), (item, key) => {
          const date = DateTime.fromJSDate(new Date(key));
          const dateFormatted = date.toFormat('MM/dd/yyyy');

          return {
            data: lodashMap(item, (item) => {
              const recordingDate = item.RecordingDate ? DateTime.fromJSDate(new Date(item.RecordingDate)) : null;
              return {
                ...item,
                RecordingDate:
                  recordingDate && recordingDate.isValid
                    ? recordingDate.toFormat('yyyy/MM/dd')
                    : 'Unknown Recording Date',
                showDocumentNumber: !(
                  item.DocumentType && item.DocumentType.toLowerCase().includes('Notice of Default'.toLowerCase())
                ),
                title: item.DocumentType
                  ? item.DocumentType
                  : date.isValid
                    ? `Transaction on ${dateFormatted}`
                    : 'Transaction'
              };
            }),
            title: date.isValid ? dateFormatted : 'Unknown Recording Date'
          };
        })
      )
    );

    this.store.pipe(select(fromDataProviderSelectors.selectIsDataTrace)).subscribe((isDataTrace) => {
      this.isDataTrace = isDataTrace;
    });

    this.store.pipe(select(fromUserSelectors.selectEmail)).subscribe((email) => {
      this.userEmail = email;
    });

    this.documentLinksEnabled$ = this.store.pipe(select(fromOfficeSelectors.selectDocumentLinksEnabled));
  }

  isDocumentValid(transactionHistoryExtended: TransactionHistoryExtended) {
    let valid: boolean;
    if (this.isDataTrace) {
      valid = !!transactionHistoryExtended.cmtIdentifier;
    } else {
      valid =
        !!transactionHistoryExtended.DocumentNum &&
        !!transactionHistoryExtended.RecordingDate &&
        transactionHistoryExtended.RecordingDate !== 'Unknown Recording Date';
    }
    return valid;
  }

  getEmailSubject(transactionHistoryExtended: TransactionHistoryExtended) {
    const defaultSubjectData: string[] = [];

    if (transactionHistoryExtended.DocumentType) {
      defaultSubjectData.push(transactionHistoryExtended.DocumentType);
    } else if (transactionHistoryExtended.title) {
      defaultSubjectData.push(transactionHistoryExtended.title);
    }

    const address = this.addressPipe.transform(this.profile, AddressEnum.LineOne);

    if (address !== '--') {
      defaultSubjectData.push('for');
      defaultSubjectData.push(address);
    }

    if (defaultSubjectData.join(' ').length > 78) {
      defaultSubjectData.splice(1, 2);
    }
    return defaultSubjectData.join(' ');
  }

  async sendDocument(transactionHistoryExtended: TransactionHistoryExtended) {
    if (this.isDocumentValid(transactionHistoryExtended)) {
      const modal = await this.modalController.create({
        component: SendDocumentModalComponent,
        componentProps: {
          email: this.userEmail || '',
          subject: this.getEmailSubject(transactionHistoryExtended)
        }
      });

      await modal.present();

      const { data, role } = await modal.onWillDismiss();

      if (role === 'confirm' && this.profile) {
        this.store.dispatch(
          fromPropertyProfileActions.emailDocument({
            payload: {
              apn: this.profile.APN ?? '',
              city: this.profile.SiteCity ?? '',
              cmtIdentifier: transactionHistoryExtended.cmtIdentifier ?? '',
              documentNumber: transactionHistoryExtended.DocumentNum ?? '',
              documentType: transactionHistoryExtended.DocumentType ?? '',
              email: data.email ?? '',
              fips: this.profile.fips ?? '',
              message: data.message ?? '',
              recodingDate: transactionHistoryExtended.RecordingDate ?? '',
              stateCode: this.profile.SiteState ?? '',
              streetAddress: this.addressPipe.transform(this.profile, AddressEnum.LineOne),
              subject: data.subject ?? ''
            }
          })
        );
      }
    } else {
      const alert = await this.alertController.create({
        buttons: [
          {
            role: 'cancel',
            text: 'Ok'
          }
        ],
        header: 'Oops',
        message: 'We are sorry, this document is unavailable.'
      });

      await alert.present();
    }
  }
}
